import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiArrowsUpDown, HiChevronUp, HiChevronDown, HiCloudArrowUp, HiMagnifyingGlass, HiArchiveBox, HiEllipsisVertical } from 'react-icons/hi2';
import { StorageDirectory, StorageItem } from '../../../types/storage';
import {
  formatFileSize,
  formatDate,
  getFileIcon,
  getFolderIcon,
  sortItems,
  sortDirectories,
  filterItemsBySearch,
  filterDirectoriesBySearch,
} from '../../../utils/storageUtils';
import {
  setCurrentDirectory,
  toggleItemSelection,
  toggleDirectorySelection,
  selectAllItems,
  clearSelection,
  setSortBy,
  setSortOrder,
  updateItemData,
  updateDirectoryData,
} from '../../../redux/slices/storageSlice';
import storageService from '../../../services/storageService';

interface FileListViewProps {
  directories: StorageDirectory[];
  items: StorageItem[];
  selectedItemIds: number[];
  selectedDirectoryIds: number[];
  sortBy: 'name' | 'date' | 'size';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  currentDirectoryId: number | null;
  onUpload: () => void;
  onContextMenu: (
    e: React.MouseEvent,
    item: StorageItem | null,
    directory: StorageDirectory | null
  ) => void;
}

function SortIcon({ column, sortBy, sortOrder }: { column: string; sortBy: string; sortOrder: string }) {
  if (sortBy !== column) {
    return <HiArrowsUpDown className="w-4 h-4 text-gray-400" />;
  }
  return sortOrder === 'asc' ? (
    <HiChevronUp className="w-4 h-4 text-primary" />
  ) : (
    <HiChevronDown className="w-4 h-4 text-primary" />
  );
}

export default function FileListView({
  directories,
  items,
  selectedItemIds,
  selectedDirectoryIds,
  sortBy,
  sortOrder,
  searchQuery,
  currentDirectoryId,
  onUpload,
  onContextMenu,
}: FileListViewProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingDirId, setEditingDirId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  // Filter and sort
  const filteredDirs = sortDirectories(
    filterDirectoriesBySearch(
      directories.filter((d) => d.parentId === currentDirectoryId),
      searchQuery
    ),
    sortBy,
    sortOrder
  );

  const filteredItems = sortItems(
    filterItemsBySearch(
      items.filter((i) => i.directoryId === currentDirectoryId),
      searchQuery
    ),
    sortBy,
    sortOrder
  );

  const hasItems = filteredDirs.length > 0 || filteredItems.length > 0;
  const allSelected =
    selectedItemIds.length === filteredItems.length && filteredItems.length > 0;

  const handleSort = (newSortBy: 'name' | 'date' | 'size') => {
    if (sortBy === newSortBy) {
      dispatch(setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      dispatch(setSortBy(newSortBy));
      dispatch(setSortOrder('asc'));
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      dispatch(clearSelection());
    } else {
      dispatch(selectAllItems());
    }
  };

  const handleNavigateToDirectory = (dirId: number) => {
    dispatch(setCurrentDirectory(dirId));
  };

  const saveEdit = async (id: number, isDirectory: boolean) => {
    if (!editValue.trim()) {
      cancelEdit();
      return;
    }

    try {
      if (isDirectory) {
        const updated = await storageService.updateDirectory(id, { name: editValue.trim() });
        dispatch(updateDirectoryData(updated));
      } else {
        const updated = await storageService.updateItem(id, { fileName: editValue.trim() });
        dispatch(updateItemData(updated));
      }
      cancelEdit();
    } catch (err) {
      console.error('Failed to rename:', err);
      cancelEdit();
    }
  };

  const cancelEdit = () => {
    setEditingItemId(null);
    setEditingDirId(null);
    setEditValue('');
  };

  if (!hasItems && searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <HiMagnifyingGlass className="w-20 h-20 mb-4 text-gray-300" />
        <p className="text-xl font-semibold text-primary-900 mb-1">{t('page.files.fileListView.noResultsTitle')}</p>
        <p className="text-sm text-gray-500">{t('page.files.fileListView.noResultsSubtitle')}</p>
      </div>
    );
  }

  if (!hasItems) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <HiArchiveBox className="w-20 h-20 mb-4 text-gray-300" />
        <p className="text-xl font-semibold text-primary-900 mb-1">{t('page.files.fileListView.emptyFolderTitle')}</p>
        <p className="text-sm text-gray-500 mb-6">{t('page.files.fileListView.emptyFolderSubtitle')}</p>
        <button
          type="button"
          onClick={onUpload}
          className="px-6 py-2.5 bg-primary text-white rounded-full hover:bg-primary-900 text-sm font-semibold transition-all hover:bg-opacity-80 flex items-center gap-2"
        >
          <HiCloudArrowUp className="w-5 h-5" />
          {t('page.files.fileListView.uploadFiles')}
        </button>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-secondary-200 overflow-hidden">
      <table className="min-w-full divide-y divide-secondary-200">
        <thead className="bg-secondary-200 bg-opacity-50">
          <tr>
            <th scope="col" className="w-12 px-6 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                className="rounded border-secondary-200 text-primary focus:ring-primary"
              />
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold text-primary-900 tracking-wider cursor-pointer hover:text-primary transition-colors"
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center gap-2">
                <span>{t('page.files.fileListView.name')}</span>
                <SortIcon column="name" sortBy={sortBy} sortOrder={sortOrder} />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold text-primary-900 tracking-wider cursor-pointer hover:text-primary transition-colors"
              onClick={() => handleSort('size')}
            >
              <div className="flex items-center gap-2">
                <span>{t('page.files.fileListView.size')}</span>
                <SortIcon column="size" sortBy={sortBy} sortOrder={sortOrder} />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold text-primary-900 tracking-wider cursor-pointer hover:text-primary transition-colors"
              onClick={() => handleSort('date')}
            >
              <div className="flex items-center gap-2">
                <span>{t('page.files.fileListView.modified')}</span>
                <SortIcon column="date" sortBy={sortBy} sortOrder={sortOrder} />
              </div>
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-primary-900 tracking-wider">
              {t('page.files.fileListView.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-200 bg-white">
          {/* Directories */}
          {filteredDirs.map((dir) => (
            <tr
              key={`dir-${dir.id}`}
              className={`hover:bg-secondary-200/30 cursor-pointer transition-all group ${
                selectedDirectoryIds.includes(dir.id) ? 'bg-primary-100' : ''
              }`}
              onContextMenu={(e) => onContextMenu(e, null, dir)}
            >
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedDirectoryIds.includes(dir.id)}
                  onChange={() => dispatch(toggleDirectorySelection(dir.id))}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-secondary-200 text-primary focus:ring-primary"
                />
              </td>
              <td
                className="px-4 py-4"
                onDoubleClick={() => handleNavigateToDirectory(dir.id)}
              >
                <div className="flex items-center gap-3">
                  {getFolderIcon()}
                  {editingDirId === dir.id ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(dir.id, true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(dir.id, true);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-1.5 border-2 border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                  ) : (
                    <span
                      className="font-semibold text-primary-900 hover:text-primary transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigateToDirectory(dir.id);
                      }}
                    >
                      {dir.name}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">—</td>
              <td className="px-4 py-4 text-sm text-gray-500">{formatDate(dir.updatedAt)}</td>
              <td className="px-4 py-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextMenu(e, null, dir);
                  }}
                  className="text-gray-400 hover:text-primary-900 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <HiEllipsisVertical className="w-5 h-5" />
                </button>
              </td>
            </tr>
          ))}

          {/* Files */}
          {filteredItems.map((item) => (
            <tr
              key={`item-${item.id}`}
              className={`hover:bg-secondary-200/30 transition-all group ${
                selectedItemIds.includes(item.id) ? 'bg-primary-100' : ''
              }`}
              onContextMenu={(e) => onContextMenu(e, item, null)}
            >
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedItemIds.includes(item.id)}
                  onChange={() => dispatch(toggleItemSelection(item.id))}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-secondary-200 text-primary focus:ring-primary"
                />
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                  {getFileIcon(item.fileName)}
                  {editingItemId === item.id ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(item.id, false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(item.id, false);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-1.5 border-2 border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                  ) : (
                    <a
                      href={item.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-900 hover:text-primary font-medium transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.fileName}
                    </a>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">{formatFileSize(item.fileSizeInBytes)}</td>
              <td className="px-4 py-4 text-sm text-gray-500">{formatDate(item.updatedAt)}</td>
              <td className="px-4 py-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextMenu(e, item, null);
                  }}
                  className="text-gray-400 hover:text-primary-900 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <HiEllipsisVertical className="w-5 h-5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
